import { useEffect, useState, type ReactNode } from "react";
import { projectReadiness, } from "../domain/project";
import type {
  CosplayProject,
  ProjectStage,
} from "../domain/types";
import { PortfolioThemeControl } from "../PortfolioThemeControl";

export const genreOptions = [
  ["Fantasy", "판타지"],
  ["Action", "액션"],
  ["Comedy", "코미디"],
  ["Drama", "드라마"],
  ["Mystery", "미스터리"],
  ["Romance", "로맨스"],
  ["Slice of Life", "일상"],
  ["Sci-Fi", "SF"],
] as const;

export const stageMeta: Record<ProjectStage, { number: string; label: string }> = {
  discover: { number: "01", label: "캐릭터" },
  prepare: { number: "02", label: "준비" },
  onsite: { number: "03", label: "현장" },
  photos: { number: "04", label: "사진" },
  archive: { number: "05", label: "기록" },
};

export const safeSourceUrl = (value: string) => { try { const url = new URL(value); return ["https:", "http:"].includes(url.protocol) ? url.href : null; } catch { return null; } };

export const won = (value: number) => `${value.toLocaleString("ko-KR")}원`;

export const downloadJson = (fileName: string, value: unknown) => {
  const url = URL.createObjectURL(new Blob([JSON.stringify(value, null, 2)], { type: "application/json" }));
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = fileName;
  anchor.click();
  URL.revokeObjectURL(url);
};

export function usePathname() {
  const [pathname, setPathname] = useState(window.location.pathname);
  useEffect(() => {
    const listener = () => setPathname(window.location.pathname);
    window.addEventListener("popstate", listener);
    return () => window.removeEventListener("popstate", listener);
  }, []);
  const navigate = (to: string) => {
    window.history.pushState({}, "", to);
    setPathname(window.location.pathname);
    window.scrollTo({ top: 0, behavior: "auto" });
  };
  return { pathname, navigate };
}

export function useOnlineStatus() {
  const [online, setOnline] = useState(navigator.onLine);
  useEffect(() => {
    const update = () => setOnline(navigator.onLine);
    window.addEventListener("online", update);
    window.addEventListener("offline", update);
    return () => {
      window.removeEventListener("online", update);
      window.removeEventListener("offline", update);
    };
  }, []);
  return online;
}

export function AppLink({ to, navigate, children, className = "" }: { to: string; navigate: (to: string) => void; children: ReactNode; className?: string }) {
  return (
    <a
      href={to}
      className={className}
      onClick={(event) => {
        if (!event.metaKey && !event.ctrlKey && !event.shiftKey) {
          event.preventDefault();
          navigate(to);
        }
      }}
    >
      {children}
    </a>
  );
}

export function SourceBadge({ source, checkedAt }: { source: string; checkedAt: string }) {
  return <span className="source-badge">{source === "해볼캐 seed" ? "해볼캐 확인본" : source === "AniList live" ? "AniList 자료" : source} · {checkedAt} 확인</span>;
}

export function PageHeader({ eyebrow, title, description, aside }: { eyebrow: string; title: string; description: string; aside?: ReactNode }) {
  return (
    <header className="page-header">
      <div>
        <p className="eyebrow">{eyebrow}</p>
        <h1>{title}</h1>
        <p className="page-lede">{description}</p>
      </div>
      {aside && <div className="page-aside">{aside}</div>}
    </header>
  );
}

export function EmptyProject({ navigate }: { navigate: (to: string) => void }) {
  return (
    <section className="empty-ticket">
      <span className="ticket-code">첫 프로젝트</span>
      <h2>홈에서 작품명과 캐릭터명을 적어 시작해요.</h2>
      <p>추천이나 로그인 없이 의상·행사·촬영·사진 권리가 연결된 준비표가 열립니다.</p>
      <button className="button primary" onClick={() => navigate("/")}>준비표 만들기</button>
      <button className="button text" onClick={() => navigate("/discover")}>아이디어 보기 →</button>
    </section>
  );
}

export function StageBoard({ project, onToggle }: { project: CosplayProject; onToggle: (taskId: string) => void }) {
  return (
    <section className="stage-board" aria-label={`${project.characterName} 프로젝트 진행판`}>
      <div className="stage-board-head">
        <div>
          <span className="ticket-code">진행 중인 프로젝트</span>
          <h2>{project.characterName} 첫 장면</h2>
          <p>{project.mediaTitle} · 촬영일 {project.dDay || "아직 미정"}</p>
        </div>
        <div className="readiness-stamp"><strong>{projectReadiness(project)}%</strong><small>할 일 체크</small></div>
      </div>
      <ol className="stage-track">
        {(Object.keys(stageMeta) as ProjectStage[]).map((stage) => {
          const tasks = project.tasks.filter((task) => task.stage === stage);
          const done = tasks.length > 0 && tasks.every((task) => task.status === "done");
          return (
            <li key={stage} className={done ? "stage complete" : "stage"}>
              <div className="stage-label"><span>{stageMeta[stage].number}</span>{stageMeta[stage].label}</div>
              {tasks.map((task) => (
                <button key={task.id} className={`task-line ${task.status}`} onClick={() => onToggle(task.id)}>
                  <span className="task-check" aria-hidden="true">{task.status === "done" ? "✓" : ""}</span>
                  <span>{task.label}</span>
                  <small>{project.dDay ? task.dueLabel : task.stage === "discover" ? "시작할 때" : "날짜 정한 뒤"}</small>
                </button>
              ))}
            </li>
          );
        })}
      </ol>
      <PortfolioThemeControl />
    </section>
  );
}


/** Content identity plus duplicate occurrence for immutable, read-only payload rows. */
export function keyedRows<T>(rows:T[]){const occurrences=new Map<string,number>();return rows.map(value=>{const content=JSON.stringify(value);const count=occurrences.get(content)||0;occurrences.set(content,count+1);return {value,key:`${content}:${count}`};});}
